import React from 'react'
import PropTypes from 'prop-types'
import CircularProgress from '@mui/material/CircularProgress'
import { Stack, Tooltip, Box } from '@mui/material'
import {
  AttachMoney as AttachMoneyIcon,
  Print as PrintIcon,
  Save as SaveIcon,
  MoreVert as MoreVertIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Info as InfoIcon,
  Email as EmailIcon,
} from '@mui/icons-material'
import { Chip, SelectChip } from 'components/generic/chip'
import {
  Incomplete as IncompleteIcon,
  Paid as PaidIcon,
  PriceComplete as PriceCompleteIcon,
  ScaleComplete as ScaleCompleteIcon,
} from 'components/assets'
import { ACTION_TYPE } from 'components/generic/buttons/ButtonList'
import { statusArray } from 'components/containers/inbound-tickets/utils'
import {
  TICKET_STATUSES,
  TICKET_TYPES,
  BILL_STATUSES,
  ENTITY_IN_LOCK_PERIOD_MESSAGE,
  CHURN_ZERO_EVENTS as cz,
} from 'utils/constants'
import PrintablePurchaseFull from 'components/generic/printable/PrintablePurchaseFull'
import PrintableScaleFull from 'components/generic/printable/PrintableScaleFull'
import { trackEvent } from 'utils/tracking'
import { DEFAULT_PDF_WIDTH } from 'utils/constants'
import { setInboundTicketId } from 'state/inboundTicket'

const getMoreActionsOptions = (setShowVoidModal, canVoid) => [
  {
    id: 'void-ticket-action',
    title: 'Void Ticket',
    disabled: !canVoid,
    onClick: () => {
      setShowVoidModal(true)
    },
  },
]

export const PRINT_ACTION = {
  ScaleFull: 0,
  PurchaseFull: 1,
}

export const iconMapper = {
  INCOMPLETE: <IncompleteIcon />,
  SCALE_COMPLETE: <ScaleCompleteIcon />,
  PRICE_COMPLETE: <PriceCompleteIcon />,
  PAID: <PaidIcon />,
}

export const renderActiveDialog = ({
  inboundTicketData,
  notes,
  ticketCreator,
  activeDialog,
  paymentData = {},
  customerData = {},
  vehicleData = {},
  pricePrepend = '$',
}) => {
  const commonProps = {
    printInboundTicketData: {
      ...inboundTicketData,
      notes: notes?.filter(note => !note.isInternal) || [],
    },
    ticketCreator,
    paymentData,
    customerData,
    vehicleData,
    pricePrepend,
  }

  switch (activeDialog.action) {
    case PRINT_ACTION.ScaleFull:
      return <PrintableScaleFull {...commonProps} />
    case PRINT_ACTION.PurchaseFull:
      return <PrintablePurchaseFull {...commonProps} />
    default:
      return null
  }
}

const Status = ({
  control,
  ticketStatus,
  canUpdateStatus,
  enableTruckInYardIBTStatusFeatureFlag,
  ticketIsLinkedToBill,
  billExternalId,
  isInLockedAccountingPeriodV1,
  billIsInvoiced,
}) => {
  if (ticketStatus === TICKET_STATUSES.PAID.id) {
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <Chip
          data-testid="ibt-status-chip"
          icon={<PaidIcon />}
          label={TICKET_STATUSES.PAID.name}
          sx={{
            fontWeight: 500,
            fontSize: '16px',
            padding: 2,
          }}
        />
        <Tooltip
          title="Paid inbound tickets are not editable. To edit a paid ticket, reverse the payment associated with this inbound ticket."
          placement="right"
          arrow
        >
          <InfoIcon color="textSecondary" />
        </Tooltip>
      </Stack>
    )
  }
  if (ticketStatus === TICKET_STATUSES.VOID.id) {
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <Chip
          data-testid="ibt-status-chip"
          label={TICKET_STATUSES.VOID.name}
          sx={{
            fontWeight: 500,
            fontSize: '16px',
            padding: 2,
          }}
        />
      </Stack>
    )
  }
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Tooltip title={isInLockedAccountingPeriodV1 ? ENTITY_IN_LOCK_PERIOD_MESSAGE : ''}>
        <div>
          <SelectChip
            data-testid="ibt-status-select-chip"
            control={control}
            items={
              enableTruckInYardIBTStatusFeatureFlag
                ? statusArray
                : statusArray.filter(status => status !== TICKET_STATUSES.TRUCK_IN_YARD)
            }
            name="status.value"
            disabled={!canUpdateStatus}
            defaultValue={statusArray[0].id}
            selectProps={{
              IconComponent: ArrowDropDownIcon,
              renderValue: value => (
                <Chip
                  icon={iconMapper[value]}
                  label={TICKET_STATUSES[value]?.name}
                  sx={{
                    border: 'none',
                    fontWeight: 500,
                    fontSize: '16px',
                  }}
                />
              ),
            }}
          />
        </div>
      </Tooltip>
      {ticketIsLinkedToBill && billExternalId && (
        <Tooltip title={`To edit this IBT, please remove it from Bill #${billExternalId}`} placement="right" arrow>
          <InfoIcon color="textSecondary" />
        </Tooltip>
      )}
      {billIsInvoiced && (
        <Tooltip title="To edit this IBT, please void the invoice that was generated from it" placement="right" arrow>
          <InfoIcon color="textSecondary" />
        </Tooltip>
      )}
    </Stack>
  )
}

Status.propTypes = {
  control: PropTypes.instanceOf(Object).isRequired,
  ticketStatus: PropTypes.string,
  canUpdateStatus: PropTypes.bool,
  enableTruckInYardIBTStatusFeatureFlag: PropTypes.bool,
  ticketIsLinkedToBill: PropTypes.bool,
  billExternalId: PropTypes.string,
  isInLockedAccountingPeriodV1: PropTypes.bool,
  billIsInvoiced: PropTypes.bool,
}

Status.defaultProps = {
  ticketStatus: 'INCOMPLETE',
  canUpdateStatus: true,
  enableTruckInYardIBTStatusFeatureFlag: false,
  ticketIsLinkedToBill: false,
  billExternalId: '',
  isInLockedAccountingPeriodV1: false,
  billIsInvoiced: false,
}

const SavingIcon = () => (
  <Box
    sx={{
      display: 'flex',
      width: '18px',
      height: '18px',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <CircularProgress style={{ width: '18px', height: '18px' }} />
  </Box>
)

const determineSaveText = (isAutoSaving, inboundTicketData, isDirty) => {
  let text

  switch (true) {
    case isAutoSaving:
      text = 'Autosaving...'
      break
    case !inboundTicketData:
      text = 'Create Ticket'
      break
    case inboundTicketData && isDirty:
      text = 'Save Ticket'
      break
    case inboundTicketData && !isDirty:
      text = 'Saved'
      break
    default:
      text = 'Saved'
      break
  }

  return text
}

const getActions = ({
  inboundTicketData,
  billData,
  isDirty,
  hasEditedMaterials,
  errors,
  setActiveDialog,
  handlePrint,
  enableFePdfs,
  setShowVoidModal,
  methods: { getValues, setValue, handleSubmit },
  onSubmit,
  navigate,
  inboundTicketId,
  isUpdate,
  canPay,
  isAutoSaving,
  canUseDefaultCustomer,
  defaultRetailCustomer,
  hasDataConflict,
  errorsTareRequired,
  isInLockedAccountingPeriodV1,
  handleOpenEmailModal,
  enablePdfEmails,
  currentTotal,
  dispatch,
  enableNegativeIbtToInvoice = false,
}) => {
  const statusIsPaidOrVoided = () =>
    inboundTicketData?.status?.value === TICKET_STATUSES.PAID.id ||
    inboundTicketData?.status?.value === TICKET_STATUSES.VOID.id

  const handleDefaultCustomer = () => {
    if (!canUseDefaultCustomer) return
    if (getValues('customer') === null) {
      setValue('customer', defaultRetailCustomer)
    } else if (getValues('customer.name') === null) {
      setValue('customer.name', defaultRetailCustomer)
    }
  }
  const isSaveDisabled =
    (!isDirty && !hasEditedMaterials) ||
    !!Object.keys(errors).length ||
    isAutoSaving ||
    hasDataConflict ||
    isInLockedAccountingPeriodV1

  const saveAndPrintAction = {
    text: 'PRINT',
    icon: <PrintIcon />,
    variant: 'outlined',
    disabled: isSaveDisabled,
    id: 'save-and-print-button',
    onClick: async e => {
      handleDefaultCustomer()
      const newOnSubmit = async data => onSubmit(data, null, 'print')
      handleSubmit(newOnSubmit)(e).then(() => {
        trackEvent(cz.SAVE_PRINT)
      })
    },
  }

  const saveAndPayAction = {
    text: 'PAY',
    icon: <AttachMoneyIcon />,
    variant: 'outlined',
    disabled: isSaveDisabled,
    id: 'save-and-pay-button',
    onClick: async e => {
      handleDefaultCustomer()
      const newOnSubmit = async data => onSubmit(data, null, 'pay')
      handleSubmit(newOnSubmit)(e).then(() => {
        trackEvent(cz.SAVE_PAY)
      })
    },
  }

  const saveAction = {
    text: determineSaveText(isAutoSaving, inboundTicketData, isDirty || hasEditedMaterials),
    icon: isAutoSaving ? <SavingIcon /> : inboundTicketData ? <SaveIcon /> : null,
    disabled: isSaveDisabled,
    id: 'save-button',
    onClick: async e => {
      handleDefaultCustomer()
      handleSubmit(onSubmit)(e).then(() => {
        trackEvent(cz.SAVE_ONLY)
      })
    },
  }

  const cancelAction = {
    text: 'Cancel',
    variant: 'text',
    id: 'cancel-button',
    onClick: () => navigate(`/app/purchases/ticket-library/${inboundTicketId || ''}`),
  }
  const doneAction = {
    text: 'Done',
    variant: 'outlined',
    id: 'done-button',
    onClick: () => navigate('/app/purchases/ticket-library'),
  }
  const canVoid = !isInLockedAccountingPeriodV1 && inboundTicketData?.ticketType !== TICKET_TYPES.TRANSFER
  const extraAction = {
    text: '',
    variant: 'text',
    id: 'more-actions-button',
    onClick: option => option.onClick(),
    type: ACTION_TYPE.SelectButton,
    endIcon: <MoreVertIcon />,
    options: getMoreActionsOptions(setShowVoidModal, canVoid),
  }
  const onMultiTicketBill = inboundTicketData?.bill?.isMultiTicket
  const statusIsPaid = inboundTicketData?.status?.value === TICKET_STATUSES.PAID.id
  const paymentPageIsLocked =
    isInLockedAccountingPeriodV1 &&
    inboundTicketData?.status?.value !== TICKET_STATUSES.PRICE_COMPLETE.id &&
    inboundTicketData?.status?.value !== TICKET_STATUSES.APPROVED.id
  const payActionIsDisabled = !statusIsPaid && !onMultiTicketBill && !canPay()

  // Use currentTotal which is passed from the Industrial container
  // Show invoice button when total is negative and not paid/voided
  // For fresh tickets: show the appropriate button based on total, but keep it disabled
  const isFreshTicket = !inboundTicketData?.id

  const shouldShowInvoiceButton = enableNegativeIbtToInvoice && (currentTotal || 0) < 0 && !statusIsPaidOrVoided()

  // Invoice button should be enabled only when there are no unsaved changes AND it's not a fresh ticket
  const invoiceButtonEnabled = shouldShowInvoiceButton && !isDirty && !hasEditedMaterials && !isFreshTicket
  const billIsInvoiced = enableNegativeIbtToInvoice && billData?.status === BILL_STATUSES.INVOICED.id
  const payAction = {
    icon: <AttachMoneyIcon />,
    text: billIsInvoiced ? 'INVOICE DETAILS' : statusIsPaid || onMultiTicketBill ? 'PAYMENT DETAILS' : 'PAY',
    variant: statusIsPaid || onMultiTicketBill || billIsInvoiced ? 'text' : 'contained',
    id: 'inbound-ticket-pay-button',
    disabled: payActionIsDisabled,
    onClick: () => {
      if (billIsInvoiced) {
        navigate(`/app/sales/invoices/${billData.invoiceId}`)
      } else if (errorsTareRequired(inboundTicketData.materials, 'scale') && !onMultiTicketBill && !statusIsPaid) {
        return
      } else if (onMultiTicketBill || billData?.status?.toUpperCase() === BILL_STATUSES.PARTIAL.id) {
        navigate(`/app/purchases/bills/${inboundTicketData?.billId}`)
      } else {
        navigate(`/app/purchases/ticket-library/${inboundTicketId}/payment`)
      }
    },
    type: ACTION_TYPE.TooltipButton,
    tooltipProps: {
      title: paymentPageIsLocked && payActionIsDisabled ? ENTITY_IN_LOCK_PERIOD_MESSAGE : '',
    },
  }

  const invoiceAction = {
    icon: <AttachMoneyIcon />,
    text: billData?.invoiceId ? 'INVOICE' : 'GENERATE INVOICE',
    variant: billData?.invoiceId ? 'text' : 'contained',
    id: 'inbound-ticket-invoice-button',
    disabled: !invoiceButtonEnabled,
    onClick: () => {
      if (billData?.invoiceId) {
        trackEvent(cz.VIEW_INVOICE)
        navigate(`/app/sales/invoices/${billData.invoiceId}`)
      } else {
        // Set the inboundTicketId in Redux state before navigating
        dispatch(setInboundTicketId(inboundTicketData?.id))
        navigate('/app/sales/invoices/new')
      }
    },
  }

  const printScaleTicketAction = {
    text: 'SCALE TICKET',
    icon: <PrintIcon />,
    variant: 'text',
    id: 'inbound-ticket-scale-print',
    type: ACTION_TYPE.Button,
    onClick: () => {
      if (enableFePdfs) {
        const docTitle = `Scale Ticket #${inboundTicketData?.ticketId} | ${
          TICKET_STATUSES[inboundTicketData?.status?.value?.toString()]?.name
        }`
        const paperWidth = localStorage.getItem('paperSize') ?? DEFAULT_PDF_WIDTH
        handlePrint(
          `/core-api/purchases/inbound-tickets/${inboundTicketId}/pdf/IBT_SCALE_TICKET?width=${paperWidth}`,
          docTitle,
        )
      } else {
        setActiveDialog({
          title: 'Scale Ticket (Full Page)',
          action: PRINT_ACTION.ScaleFull,
          dialogTitle: `Scale Ticket #${inboundTicketData?.ticketId} | ${
            TICKET_STATUSES[inboundTicketData?.status?.value?.toString()]?.name
          }`,
        })
      }
    },
  }

  const printPurchaseTicketAction = {
    text: 'PURCHASE TICKET',
    icon: <PrintIcon />,
    variant: 'text',
    id: 'inbound-ticket-purchase-print',
    type: ACTION_TYPE.Button,
    onClick: () => {
      if (enableFePdfs) {
        const docTitle = `Purchase Ticket #${inboundTicketData?.ticketId} | ${
          TICKET_STATUSES[inboundTicketData?.status?.value?.toString()]?.name
        }`
        const paperWidth = localStorage.getItem('paperSize') ?? DEFAULT_PDF_WIDTH
        handlePrint(
          `/core-api/purchases/inbound-tickets/${inboundTicketId}/pdf/IBT_PURCHASE_TICKET?width=${paperWidth}`,
          docTitle,
        )
      } else {
        setActiveDialog({
          title: 'Purchase Ticket (Full Page)',
          action: PRINT_ACTION.PurchaseFull,
          dialogTitle: `Purchase Ticket #${inboundTicketData?.ticketId} | ${
            TICKET_STATUSES[inboundTicketData?.status?.value?.toString()]?.name
          }`,
        })
      }
    },
  }

  const emailAction = {
    text: 'EMAIL',
    icon: <EmailIcon />,
    variant: 'text',
    id: 'inbound-ticket-email-button',
    type: ACTION_TYPE.Button,
    onClick: () => handleOpenEmailModal(),
  }

  const dividerAction = {
    type: ACTION_TYPE.Divider,
  }

  let actionsList = []

  if (hasDataConflict) {
    actionsList = []
  } else if (!isUpdate) {
    actionsList = [
      saveAndPrintAction,
      ...(shouldShowInvoiceButton ? [invoiceAction] : [saveAndPayAction]),
      saveAction,
      cancelAction,
    ]
  } else if (inboundTicketData?.status?.value === TICKET_STATUSES.VOID.id) {
    actionsList = [
      ...(enablePdfEmails
        ? [emailAction, printScaleTicketAction, printPurchaseTicketAction]
        : [printScaleTicketAction, printPurchaseTicketAction]),
      doneAction,
    ]
  } else if (statusIsPaid && isDirty) {
    actionsList = [saveAndPrintAction, saveAction, cancelAction]
  } else if (isDirty || hasEditedMaterials) {
    // When there are unsaved changes, show appropriate action based on total
    // If negative total: show disabled GENERATE INVOICE, if positive: show PAY
    actionsList = [
      saveAndPrintAction,
      ...(shouldShowInvoiceButton ? [invoiceAction] : [saveAndPayAction]),
      saveAction,
      cancelAction,
      extraAction,
    ]
  } else if (statusIsPaid) {
    actionsList = [
      ...(shouldShowInvoiceButton ? [invoiceAction] : [payAction]),
      ...(enablePdfEmails
        ? [dividerAction, emailAction, printScaleTicketAction, printPurchaseTicketAction]
        : [printScaleTicketAction, printPurchaseTicketAction]),
    ]
  } else {
    actionsList = [
      saveAction,
      ...(shouldShowInvoiceButton ? [invoiceAction] : [payAction]),
      ...(enablePdfEmails
        ? [dividerAction, emailAction, printScaleTicketAction, printPurchaseTicketAction]
        : [printScaleTicketAction, printPurchaseTicketAction]),
      doneAction,
      extraAction,
    ]
  }

  return actionsList
}

export const updateTicketActionsAndStatus = ticketStatusProps => {
  const {
    contentContext,
    control,
    canUpdateStatus,
    enableTruckInYardIBTStatusFeatureFlag,
    enableMaterialPaymentHolds,
    enableNegativeIbtToInvoice = false,
    dispatch,
    ...getActionsProps
  } = ticketStatusProps
  const { inboundTicketData, billData, isInLockedAccountingPeriodV1 } = getActionsProps

  contentContext.setContainerStatus(
    <Status
      control={control}
      ticketStatus={inboundTicketData?.status?.value}
      ticketIsLinkedToBill={enableMaterialPaymentHolds ? !!billData?.isMultiTicket : false}
      billExternalId={enableMaterialPaymentHolds ? String(billData?.externalId || '') : ''}
      canUpdateStatus={canUpdateStatus}
      enableTruckInYardIBTStatusFeatureFlag={enableTruckInYardIBTStatusFeatureFlag}
      isInLockedAccountingPeriodV1={isInLockedAccountingPeriodV1}
      billIsInvoiced={enableNegativeIbtToInvoice && billData?.status === BILL_STATUSES.INVOICED.id}
    />,
  )

  contentContext.setContainerActions(getActions({ ...getActionsProps, dispatch, enableNegativeIbtToInvoice }))
}
